import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const PerformanceHistoryTab = ({ profile }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('6months');
  const [selectedMetric, setSelectedMetric] = useState('all');

  const periodOptions = [
    { value: '1month', label: 'Dernier mois' },
    { value: '3months', label: '3 derniers mois' },
    { value: '6months', label: '6 derniers mois' },
    { value: '1year', label: 'Dernière année' },
    { value: 'all', label: 'Toute la période' }
  ];

  const metricOptions = [
    { value: 'all', label: 'Toutes les métriques' },
    { value: 'bookings', label: 'Réservations' },
    { value: 'revenue', label: 'Revenus' },
    { value: 'satisfaction', label: 'Satisfaction client' },
    { value: 'referrals', label: 'Recommandations' }
  ];

  // Mock performance data
  const performanceStats = {
    totalBookings: 127,
    successRate: 94.5,
    totalRevenue: 45680,
    averageRating: 4.8,
    totalReferrals: 23,
    repeatClients: 67
  };

  const achievements = [
    {
      id: 1,
      title: 'Top Performer',
      description: 'Parmi les 10% meilleurs partenaires ce mois',
      icon: 'Trophy',
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-50',
      earnedDate: '2024-08-15'
    },
    {
      id: 2,
      title: 'Client Satisfaction',
      description: 'Note moyenne supérieure à 4.5/5',
      icon: 'Star',
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      earnedDate: '2024-07-20'
    },
    {
      id: 3,
      title: 'Reliable Partner',
      description: '100% de taux de réussite sur 50 réservations',
      icon: 'Shield',
      color: 'text-green-500',
      bgColor: 'bg-green-50',
      earnedDate: '2024-06-10'
    },
    {
      id: 4,
      title: 'Referral Master',
      description: 'Plus de 20 recommandations réussies',
      icon: 'Users',
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
      earnedDate: '2024-05-05'
    }
  ];

  const recentBookings = [
    {
      id: 1,
      venue: 'Le Warehouse',
      event: 'Soirée House Music',
      date: '2024-09-01',
      status: 'completed',
      rating: 5,
      revenue: 850,
      clientFeedback: `Excellente prestation ! L'ambiance était parfaite et les invités ont adoré. Nous recommandons vivement ce DJ pour des événements similaires.`
    },
    {
      id: 2,
      venue: 'Club Parisien',event: 'Weekend Techno',date: '2024-08-28',status: 'completed',rating: 4,revenue: 1200,clientFeedback: `Très bonne soirée, le DJ a su s'adapter au public. Quelques petits ajustements sur le volume mais dans l'ensemble très satisfait.`
    },
    {
      id: 3,
      venue: 'Festival Électro',event: 'Main Stage Set',date: '2024-08-15',status: 'completed',
      rating: 5,
      revenue: 2500,
      clientFeedback: `Performance exceptionnelle ! Le public était en délire du début à la fin. Définitivement notre premier choix pour l'année prochaine.`
    },
    {
      id: 4,
      venue: 'Rooftop Sunset',
      event: 'After Work Party',
      date: '2024-08-10',
      status: 'completed',
      rating: 4,
      revenue: 650,
      clientFeedback: `Ambiance parfaite pour un after work. Musique adaptée et transition fluide entre les différents moments de la soirée.`
    }
  ];

  const testimonials = [
    {
      id: 1,
      client: 'Marie Dubois',
      venue: 'Le Warehouse',
      rating: 5,
      comment: `Collaboration exceptionnelle ! Professionnel, ponctuel et à l'écoute de nos besoins. La soirée a été un véritable succès grâce à son talent.`,date: '2024-08-20',avatar: 'https://randomuser.me/api/portraits/women/32.jpg'
    },
    {
      id: 2,
      client: 'Thomas Martin',venue: 'Club Parisien',
      rating: 5,
      comment: `Un DJ qui sait lire son public et créer une atmosphère unique. Nous avons eu de nombreux retours positifs de nos clients.`,
      date: '2024-08-05',avatar: 'https://randomuser.me/api/portraits/men/45.jpg'
    },
    {
      id: 3,
      client: 'Sophie Laurent',venue: 'Festival Électro',rating: 4,comment: `Très bonne prestation technique et artistique. L'énergie était au rendez-vous et le set parfaitement adapté à notre événement.`,
      date: '2024-07-15',
      avatar: 'https://randomuser.me/api/portraits/women/28.jpg'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success bg-success/10';
      case 'pending':
        return 'text-warning bg-warning/10';
      case 'cancelled':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Terminé';
      case 'pending':
        return 'En attente';
      case 'cancelled':
        return 'Annulé';
      default:
        return 'Inconnu';
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Icon
        key={i}
        name="Star"
        size={16}
        className={i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}
      />
    ));
  };

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-xl font-semibold text-foreground">Historique des Performances</h2>
        <div className="flex gap-3">
          <Select
            options={periodOptions}
            value={selectedPeriod}
            onChange={setSelectedPeriod}
            className="w-40"
          />
          <Select
            options={metricOptions}
            value={selectedMetric}
            onChange={setSelectedMetric}
            className="w-48"
          />
        </div>
      </div>
      {/* Performance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Calendar" size={20} className="text-accent" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{performanceStats?.totalBookings}</p>
              <p className="text-sm text-muted-foreground">Réservations totales</p>
            </div>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={20} className="text-success" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{performanceStats?.successRate}%</p>
              <p className="text-sm text-muted-foreground">Taux de réussite</p>
            </div>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Euro" size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">€{performanceStats?.totalRevenue?.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">Revenus totaux</p>
            </div>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center">
              <Icon name="Star" size={20} className="text-yellow-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{performanceStats?.averageRating}</p>
              <p className="text-sm text-muted-foreground">Note moyenne</p>
            </div>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
              <Icon name="Users" size={20} className="text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{performanceStats?.totalReferrals}</p>
              <p className="text-sm text-muted-foreground">Recommandations</p>
            </div>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <Icon name="Repeat" size={20} className="text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{performanceStats?.repeatClients}</p>
              <p className="text-sm text-muted-foreground">Clients récurrents</p>
            </div>
          </div>
        </div>
      </div>
      {/* Achievements */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Récompenses & Badges</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {achievements?.map((achievement) => (
            <div key={achievement?.id} className="bg-surface border border-border rounded-lg p-4">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 ${achievement?.bgColor} rounded-lg flex items-center justify-center`}>
                  <Icon name={achievement?.icon} size={24} className={achievement?.color} />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">{achievement?.title}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{achievement?.description}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Obtenu le {new Date(achievement.earnedDate)?.toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Recent Bookings */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Réservations récentes</h3>
        <div className="space-y-4">
          {recentBookings?.map((booking) => (
            <div key={booking?.id} className="bg-surface border border-border rounded-lg p-4">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-medium text-foreground">{booking?.venue}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking?.status)}`}>
                      {getStatusText(booking?.status)}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">{booking?.event}</p>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="Calendar" size={14} />
                      <span>{new Date(booking.date)?.toLocaleDateString('fr-FR')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Euro" size={14} />
                      <span>€{booking?.revenue}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {renderStars(booking?.rating)}
                    </div>
                  </div>
                </div>
                <div className="lg:w-80">
                  <p className="text-sm text-muted-foreground italic">
                    "{booking?.clientFeedback}"
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Client Testimonials */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Témoignages clients</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {testimonials?.map((testimonial) => (
            <div key={testimonial?.id} className="bg-surface border border-border rounded-lg p-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-muted">
                  <Image
                    src={testimonial?.avatar}
                    alt={`Photo de ${testimonial?.client}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-medium text-foreground">{testimonial?.client}</h4>
                      <p className="text-sm text-muted-foreground">{testimonial?.venue}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {renderStars(testimonial?.rating)}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground italic mb-2">
                    "{testimonial?.comment}"
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(testimonial.date)?.toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Export Options */}
      <div className="bg-muted rounded-lg p-4">
        <h3 className="font-medium text-foreground mb-3">Exporter les données</h3>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
          >
            Rapport PDF
          </Button>
          <Button
            variant="outline"
            iconName="FileSpreadsheet"
            iconPosition="left"
          >
            Export Excel
          </Button>
          <Button
            variant="outline"
            iconName="Share"
            iconPosition="left"
          >
            Partager le profil
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PerformanceHistoryTab;